import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { etudiantsAPI } from '../services/api';
import { departementCourant, peutModifier } from '../services/droits';
import { analyserScodocEtudiants, analyserCollageScodocEtudiants } from '../services/scodocEtudiantsImport';
import { Ecarts, LIBELLES_REGIME } from './ImportApogee';

/**
 * IMPORT SCODOC — ÉTUDIANTS (lot E.3, révision « SCODOC crée les étudiants · parcours » du 2026-10-28)
 * ==================================================================================================
 * Maquette : etudiants_maquette_v5.html, carte « Import SCODOC — Étudiants » (validée le 2026-10-28) · Spec :
 * etudiants.md § Révision du 2026-10-28 · Serveur : etudiantsFusionService (aperçu et écriture par le même calcul).
 *
 * L'export d'ADMISSION de SCODOC (44 colonnes) crée et complète les étudiants — « compléter sans écraser » : un champ vide
 * se remplit, une valeur présente n'est jamais remplacée, une contradiction est un ÉCART listé, jamais écrit.
 *   ① SCODOC exporte UN FICHIER PAR FORMATION sans dire laquelle : on la choisit (BUT1, BUT2…) ;
 *   ② la colonne « groupes » porte plusieurs partitions (« TP FI - FIA:TP QMI FA;Parcours:QMI ») : chaque VALEUR s'associe
 *      SUR PLACE à un groupe + régime et/ou un parcours (le mapping du Paramétrage, même route), et se corrige (« Modifier ») ;
 *   ③ l'aperçu commun aux deux imports : Créés · Complétés · Inchangés · Écarts signalés · En attente / refusés.
 * Écart assumé avec la maquette : pas de bouton radio « la partition qui donne le groupe » — chaque valeur dit elle-même ce
 * qu'elle donne ; les partitions qui ne donnent rien (TD, CM…) viennent APRÈS celles qui servent, sous un séparateur.
 */
const messageErreur = (e, repli) => e?.response?.data?.error || e?.message || repli;
const EXTENSIONS = ['xlsx', 'xls', 'csv'];
const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

function ImportScodocEtudiants({ user, config, actif, anneeId, annees }) {
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'etudiants');
    const anneeActive = (annees || []).find((a) => a.id === anneeId) || null;

    const [param, setParam] = useState(null);            // niveaux, formations, parcours (Paramétrage)
    const [mode, setMode] = useState('fichier');
    const [colle, setColle] = useState('');
    const [fichierNom, setFichierNom] = useState(null);
    const [analyse, setAnalyse] = useState(null);
    const [niveaux, setNiveaux] = useState([]);           // ① les formations choisies (ids de niveaux)
    const [apercu, setApercu] = useState(null);
    const [bilan, setBilan] = useState(null);
    const [occupe, setOccupe] = useState(false);
    const [erreur, setErreur] = useState(null);

    useEffect(() => {
        if (!actif || param || !departementId) return;
        etudiantsAPI.getParametrage(departementId)
            .then(({ data }) => setParam({ niveaux: data.niveaux || [], formations: data.formations || [], parcours: data.parcours || [] }))
            .catch((e) => setErreur(messageErreur(e, 'Impossible de lire le paramétrage')));
    }, [actif, param, departementId]);

    const recommencer = useCallback(() => {
        setFichierNom(null); setAnalyse(null); setApercu(null); setBilan(null); setErreur(null); setColle(''); setNiveaux([]);
    }, []);

    const lireFichier = useCallback(async (fichier) => {
        setErreur(null);
        const ext = (fichier.name.split('.').pop() || '').toLowerCase();
        if (!EXTENSIONS.includes(ext)) { setErreur(`Format non supporté : « .${ext} »`); return; }
        setOccupe(true);
        try {
            const XLSX = await import('xlsx');
            const wb = XLSX.read(new Uint8Array(await fichier.arrayBuffer()), { type: 'array' });
            // ScoDoc : « seule la première feuille du classeur sera utilisée »
            const matrice = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true, defval: null, blankrows: true });
            setFichierNom(fichier.name); setBilan(null); setApercu(null);
            setAnalyse(analyserScodocEtudiants(matrice));
        } catch (e) {
            setErreur(messageErreur(e, "Ce fichier n'a pas pu être lu"));
        } finally {
            setOccupe(false);
        }
    }, []);

    // ── L'aperçu : dès que la lecture est saine ET qu'au moins une formation est choisie ───────────────────────
    const demanderApercu = useCallback(async () => {
        if (!analyse || analyse.bloquant || !niveaux.length) return;
        setOccupe(true); setErreur(null);
        try {
            const { data } = await etudiantsAPI.apercuImportScodocEtudiants(departementId, anneeId, niveaux, analyse.lignes);
            setApercu(data);
        } catch (e) {
            setErreur(messageErreur(e, "L'aperçu a échoué"));
        } finally {
            setOccupe(false);
        }
    }, [analyse, niveaux, departementId, anneeId]);
    useEffect(() => {
        if (analyse && !analyse.bloquant && niveaux.length && !apercu && !occupe && !bilan) demanderApercu();
    }, [analyse, niveaux, apercu, occupe, bilan, demanderApercu]);

    const basculerNiveau = (id) => { setApercu(null); setNiveaux((n) => (n.includes(id) ? n.filter((x) => x !== id) : [...n, id])); };

    /** ② Associer (ou corriger) une valeur : même route que le Paramétrage, puis l'aperçu se recalcule. */
    const associer = useCallback(async (corps) => {
        setOccupe(true); setErreur(null);
        try {
            await etudiantsAPI.creerMappingScodoc(departementId, corps);
        } catch (e) {
            setErreur(messageErreur(e, "Cette valeur n'a pas pu être associée"));
        } finally {
            setOccupe(false);
            setApercu(null);
        }
    }, [departementId]);

    const importer = useCallback(async () => {
        setOccupe(true); setErreur(null);
        try {
            const { data } = await etudiantsAPI.importerScodocEtudiants(departementId, anneeId, niveaux, analyse.lignes,
                mode === 'coller' ? null : fichierNom, mode);
            setBilan(data);
        } catch (e) {
            setErreur(messageErreur(e, "L'import a échoué"));
        } finally {
            setOccupe(false);
        }
    }, [departementId, anneeId, niveaux, analyse, mode, fichierNom]);

    const aImporter = apercu ? apercu.nouveaux.length + apercu.modifies.length + apercu.identiques.length : 0;

    return (
        <div className="etu-carte etu-carte-scodoc-etudiants">
            <div className="etu-carte-entete etu-entete-scodoc">
                <i className="fa-solid fa-user-plus" /> Import SCODOC — Étudiants
                <span className="etu-entete-note">Crée · complète sans écraser</span>
            </div>
            <div className="etu-import-corps">
                {erreur && <div className="etu-statut etu-statut-ko"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}

                {/* Le fichier ou le collage */}
                {!analyse && !bilan && (
                    <>
                        <div className="etu-modes">
                            <button type="button" className={'etu-mode etu-mode-scodoc' + (mode === 'fichier' ? ' active' : '')} onClick={() => { recommencer(); setMode('fichier'); }}>
                                <i className="fa-solid fa-file-excel" /> Fichier .xlsx
                            </button>
                            <button type="button" className={'etu-mode etu-mode-scodoc' + (mode === 'coller' ? ' active' : '')} onClick={() => { recommencer(); setMode('coller'); }}>
                                <i className="fa-solid fa-clipboard" /> Coller (Ctrl+V)
                            </button>
                        </div>
                        {mode === 'fichier' ? (
                            <label className={'etu-depot-scodoc' + (!modifiable ? ' inactif' : '')}>
                                <i className="fa-solid fa-file-arrow-up" />
                                <span>Choisir l'export d'admission SCODOC (une formation par fichier)</span>
                                <input type="file" accept=".xlsx,.xls,.csv" disabled={!modifiable || occupe}
                                       onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) lireFichier(f); e.target.value = ''; }} />
                            </label>
                        ) : (
                            <>
                                <textarea className="etu-coller-zone" value={colle} onChange={(e) => setColle(e.target.value)} disabled={!modifiable}
                                          placeholder={'Collez ici (Ctrl+V)…\n\nLa première ligne collée doit être celle des intitulés de colonnes.'} />
                                <div className="etu-import-actions">
                                    <button type="button" className="etu-btn etu-btn-scodoc" disabled={!modifiable || !colle.trim()}
                                            onClick={() => { setBilan(null); setApercu(null); setAnalyse(analyserCollageScodocEtudiants(colle)); }}>
                                        <i className="fa-solid fa-eye" /> Aperçu &amp; importer
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}

                {analyse && !bilan && (
                    <>
                        <div className="etu-apercu-entete etu-apercu-scodoc">
                            <i className={'fa-solid ' + (mode === 'coller' ? 'fa-clipboard' : 'fa-file-excel')} />
                            <strong>{mode === 'coller' ? 'Collage depuis Excel' : fichierNom}</strong>
                            <span className="etu-apercu-sep">·</span>
                            <span>{pluriel(analyse.nbLignes, 'ligne', 'lignes')} · {pluriel(analyse.lues.length, 'colonne lue', 'colonnes lues')}</span>
                            {anneeActive && <span className="etu-apercu-annee"><i className="fa-solid fa-calendar" /> {anneeActive.libelle}</span>}
                            <button type="button" className="etu-btn etu-btn-neutre etu-btn-petit" onClick={recommencer} disabled={occupe}>changer</button>
                        </div>
                        {analyse.bloquant && <div className="etu-statut etu-statut-ko etu-statut-bloc"><i className="fa-solid fa-circle-exclamation" /> {analyse.bloquant}</div>}

                        {!analyse.bloquant && param && (
                            <>
                                {/* ① La formation : le fichier ne la dit pas */}
                                <div className="etu-formation-choix">
                                    <div><span className="etu-etape-num etu-etape-num-scodoc">1</span> <strong>Ce fichier ne dit pas à quelle formation il appartient.</strong>{' '}
                                        SCODOC exporte une formation par fichier : indiquez laquelle (ou lesquelles).</div>
                                    <div className="etu-formation-cases">
                                        {param.niveaux.map((n) => (
                                            <label key={n.id}><input type="checkbox" checked={niveaux.includes(n.id)} disabled={occupe} onChange={() => basculerNiveau(n.id)} /> {n.code}</label>
                                        ))}
                                    </div>
                                </div>

                                {occupe && !apercu && <div className="etu-aide etu-aide-bloc"><i className="fa-solid fa-spinner fa-spin" /> Comparaison avec la base…</div>}

                                {apercu && (
                                    <>
                                        <GroupesDuFichier partitions={apercu.partitions || []} niveauxChoisis={niveaux} param={param}
                                                          modifiable={modifiable} occupe={occupe} surAssocier={associer} />
                                        <div className="etu-etape-titre">
                                            <span className="etu-etape-num etu-etape-num-scodoc">3</span> <strong>Ce que l'import fera</strong>
                                            <span className="etu-aide"> — rien n'est écrit avant « Importer »</span>
                                        </div>
                                        <Compteurs a={apercu} />
                                        <Ecarts ecarts={apercu.ecarts} />
                                        <Messages a={apercu} />
                                        <div className="etu-import-actions">
                                            <button type="button" className="etu-btn etu-btn-scodoc" disabled={!modifiable || occupe || aImporter === 0} onClick={importer}>
                                                <i className="fa-solid fa-user-plus" /> Importer {pluriel(aImporter, 'étudiant', 'étudiants')}
                                            </button>
                                            <button type="button" className="etu-btn etu-btn-neutre" onClick={recommencer} disabled={occupe}><i className="fa-solid fa-xmark" /> Annuler</button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <div className="etu-aide etu-aide-bloc etu-scodoc-rgpd">
                            <i className="fa-solid fa-shield-halved" /> <strong>Ce qui entre en base</strong> : {analyse.lues.map((c) => c.libelle).join(', ')}.
                            {' '}<strong>Jamais</strong> : {analyse.nonLues.length ? pluriel(analyse.nonLues.length, 'autre colonne', 'autres colonnes') + ' (domicile, lieu de naissance, lycée, téléphone fixe, notes…)' : 'rien d\'autre'} —
                            {' '}elles ne quittent pas votre poste. « statut » ne fait que contrôler le régime.
                        </div>
                    </>
                )}

                {bilan && (
                    <>
                        <div className="etu-bilan-entete">
                            <i className="fa-solid fa-circle-check" /> Import fait — {mode === 'coller' ? 'collage' : bilan.fichier_nom}
                        </div>
                        <Compteurs a={bilan} />
                        <Ecarts ecarts={bilan.ecarts} />
                        <Messages a={bilan} />
                        <div className="etu-import-actions">
                            <button type="button" className="etu-btn etu-btn-neutre" onClick={recommencer}>
                                <i className="fa-solid fa-rotate-left" /> {mode === 'coller' ? 'Coller un autre tableau' : 'Importer un autre fichier'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/** Les 5 compteurs — le même vocabulaire que l'import APOGEE. */
function Compteurs({ a }) {
    return (
        <div className="etu-compteurs">
            <div className="etu-compteur"><div className="etu-compteur-nb etu-nb-crees">{a.nouveaux.length}</div><div className="etu-compteur-label">Créés</div></div>
            <div className="etu-compteur"><div className="etu-compteur-nb etu-nb-majs">{a.modifies.length}</div><div className="etu-compteur-label">Complétés</div></div>
            <div className="etu-compteur"><div className="etu-compteur-nb etu-nb-identiques">{a.identiques.length}</div><div className="etu-compteur-label">Inchangés</div></div>
            <div className="etu-compteur"><div className="etu-compteur-nb etu-nb-ecarts">{(a.ecarts || []).length}</div><div className="etu-compteur-label">Écarts signalés</div></div>
            <div className="etu-compteur"><div className="etu-compteur-nb etu-nb-erreurs">{a.refuses.length + (a.attente || []).length}</div><div className="etu-compteur-label">En attente / refusés</div></div>
        </div>
    );
}

/** Ce qui n'entre pas, et pourquoi : refus, lignes en attente, parcours absent du Programme, régime divergent. */
function Messages({ a }) {
    const [ouvert, setOuvert] = useState(false);
    const lignes = [
        ...a.refuses.map((x) => ({ ...x, genre: 'refus' })),
        ...(a.attente || []).map((x) => ({ ...x, genre: 'attente' })),
        ...(a.avertissements || []).map((x) => ({ ...x, genre: 'avert' })),
        ...(a.regimesDivergents || []).map((x) => ({ ...x, genre: 'avert' }))
    ];
    if (!lignes.length) return null;
    const attente = (a.attente || []).length;
    return (
        <div className="etu-messages-scodoc">
            {attente > 0 && (
                <div className="etu-codes-attente"><i className="fa-solid fa-triangle-exclamation" />{' '}
                    {pluriel(attente, 'étudiant attend', 'étudiants attendent')} : aucun de ses groupes n'est associé — associez une valeur à l'étape ②.</div>
            )}
            <button type="button" className="etu-btn etu-btn-neutre etu-btn-petit" onClick={() => setOuvert((v) => !v)}>
                <i className={'fa-solid ' + (ouvert ? 'fa-chevron-up' : 'fa-chevron-down')} /> {ouvert ? 'Masquer le détail' : `Détail (${lignes.length})`}
            </button>
            {ouvert && (
                <div className="etu-detail-lignes">
                    {lignes.map((x, i) => (
                        <div key={i} className={'etu-detail-ligne ' + (x.genre === 'refus' ? 'etu-detail-refus' : 'etu-detail-avert')}>
                            <i className={'fa-solid ' + (x.genre === 'refus' ? 'fa-circle-xmark' : 'fa-triangle-exclamation')} />
                            <span className="etu-detail-num">L.{x.ligne}</span><span>{x.message}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * ② Les groupes du fichier, partition par partition. Une valeur associée : son groupe, son régime, son parcours, et
 * « Modifier ». Une valeur nouvelle : elle s'associe ICI. Les partitions qui ne donnent rien viennent après celles qui servent.
 */
function GroupesDuFichier({ partitions, niveauxChoisis, param, modifiable, occupe, surAssocier }) {
    const [choix, setChoix] = useState({});
    const [edition, setEdition] = useState(null);
    const codesNiveaux = useMemo(() => param.niveaux.filter((n) => niveauxChoisis.includes(n.id)).map((n) => n.code), [param, niveauxChoisis]);
    const formations = param.formations.filter((f) => codesNiveaux.includes(f.niveau_code));
    const niveauDeFormation = (id) => (param.niveaux.find((n) => n.code === (param.formations.find((f) => String(f.id) === String(id)) || {}).niveau_code) || {}).id;
    const cle = (p, v) => p + '|' + v;
    const regle = (k, champ, val) => setChoix((x) => ({ ...x, [k]: { ...(x[k] || {}), [champ]: val } }));
    const sert = (p) => p.valeurs.some((v) => v.mappings.length);
    const utiles = partitions.filter(sert);
    const autres = partitions.filter((p) => !sert(p));
    // Toutes visibles : d'abord celles qui servent, puis les autres sous un séparateur (les replier cachait « Parcours »
    // dès que les groupes TP étaient associés — relu en préparant le banc).
    const visibles = [...utiles, ...autres];

    const valider = (p, v, ch) => {
        const niveau_id = ch.sous_niveau_id ? niveauDeFormation(ch.sous_niveau_id) : (ch.niveau_id || (niveauxChoisis.length === 1 ? niveauxChoisis[0] : null));
        setEdition(null);
        surAssocier({ niveau_id, partition: p.partition, valeur: v.valeur, sous_niveau_id: ch.sous_niveau_id || null,
            regime_inscription: ch.sous_niveau_id ? ch.regime_inscription : null, parcours_code: ch.parcours_code || null });
    };
    const pret = (ch) => (ch.sous_niveau_id && ch.regime_inscription)
        || (!ch.sous_niveau_id && ch.parcours_code && (niveauxChoisis.length === 1 || ch.niveau_id));

    const menus = (k, ch) => (<>
        <td>
            <select value={ch.sous_niveau_id || ''} disabled={!modifiable || occupe} aria-label="Groupe" onChange={(e) => regle(k, 'sous_niveau_id', e.target.value)}>
                <option value="">— à associer —</option>
                {formations.map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
            </select>
            {!ch.sous_niveau_id && niveauxChoisis.length > 1 && (
                <select value={ch.niveau_id || ''} disabled={!modifiable || occupe} aria-label="Formation" onChange={(e) => regle(k, 'niveau_id', e.target.value)}>
                    <option value="">formation…</option>
                    {param.niveaux.filter((n) => niveauxChoisis.includes(n.id)).map((n) => <option key={n.id} value={n.id}>{n.code}</option>)}
                </select>
            )}
        </td>
        <td>
            <select value={ch.regime_inscription || ''} disabled={!modifiable || occupe || !ch.sous_niveau_id} aria-label="Régime" onChange={(e) => regle(k, 'regime_inscription', e.target.value)}>
                <option value="">régime…</option>
                {Object.entries(LIBELLES_REGIME).map(([r, l]) => <option key={r} value={r}>{l}</option>)}
            </select>
        </td>
        <td>
            <select value={ch.parcours_code || ''} disabled={!modifiable || occupe} aria-label="Parcours" onChange={(e) => regle(k, 'parcours_code', e.target.value)}>
                <option value="">—</option>
                {param.parcours.map((pc) => <option key={pc.id} value={pc.code}>{pc.code}</option>)}
                {ch.parcours_code && !param.parcours.some((pc) => pc.code === ch.parcours_code) && <option value={ch.parcours_code}>{ch.parcours_code}</option>}
            </select>
        </td>
    </>);

    return (
        <div className="etu-codes etu-groupes-scodoc">
            <div className="etu-etape-titre">
                <span className="etu-etape-num etu-etape-num-scodoc">2</span> <strong>Les groupes du fichier</strong>
                <span className="etu-aide"> — associez chaque valeur ; ce qui est déjà connu est prérempli et mémorisé dans le Paramétrage.</span>
            </div>
            <table className="etu-table etu-table-codes">
                <thead><tr><th>Partition</th><th>Valeur</th><th className="etu-col-nb">Étud.</th><th>→ Groupe</th><th>Régime</th><th>Parcours</th><th /></tr></thead>
                <tbody>
                    {visibles.map((p, ip) => [ip === utiles.length && utiles.length > 0 && autres.length > 0 && (
                        <tr key={'sep-' + ip} className="etu-separateur"><td colSpan={7}>Partitions sans association — facultatif : une ligne n'a besoin que d'<strong>un</strong> groupe ; un parcours peut venir d'ici.</td></tr>
                    ), ...p.valeurs.map((v, i) => {
                        const k = cle(p.partition, v.valeur);
                        const m = v.mappings[0];
                        const ch = choix[k] || {};
                        const partitionCellule = i === 0 && <td rowSpan={p.valeurs.length} className="etu-partition"><strong>{p.partition || '(sans partition)'}</strong></td>;
                        if (m && edition !== k) {
                            return (
                                <tr key={k}>
                                    {partitionCellule}
                                    <td className="etu-mono">{v.valeur}</td><td className="etu-col-nb">{v.nb}</td>
                                    <td>{m.sous_niveau_id ? <><span className="etu-badge-exemple">{m.formation_nom}</span> <i className="fa-solid fa-memory etu-memorise" title="Mémorisé dans le Paramétrage" /></> : <span className="etu-aide">—</span>}</td>
                                    <td>{m.regime_inscription ? <span className={'etu-tag-regime etu-regime-' + m.regime_inscription}>{LIBELLES_REGIME[m.regime_inscription]}</span> : <span className="etu-aide">—</span>}</td>
                                    <td>{m.parcours_code ? <strong>{m.parcours_code}</strong> : <span className="etu-aide">—</span>}</td>
                                    <td>{modifiable && (
                                        <button type="button" className="etu-btn etu-btn-neutre etu-btn-petit" disabled={occupe || edition !== null}
                                                onClick={() => { setChoix((x) => ({ ...x, [k]: { sous_niveau_id: m.sous_niveau_id ? String(m.sous_niveau_id) : '', regime_inscription: m.regime_inscription || '', parcours_code: m.parcours_code || '', niveau_id: '' } })); setEdition(k); }}>
                                            <i className="fa-solid fa-pen" /> Modifier
                                        </button>)}
                                    </td>
                                </tr>
                            );
                        }
                        return (
                            <tr key={k} className={m ? 'etu-code-edition' : 'etu-code-attente-doux'}>
                                {partitionCellule}
                                <td className="etu-mono">{v.valeur}</td><td className="etu-col-nb">{v.nb}</td>
                                {menus(k, ch)}
                                <td className="etu-codes-actions">
                                    <button type="button" className="etu-btn etu-btn-scodoc etu-btn-petit" disabled={!pret(ch) || occupe || !modifiable}
                                            title={pret(ch) ? 'Enregistrer et refaire l\'aperçu' : 'Choisissez un groupe et son régime, ou un parcours'}
                                            onClick={() => valider(p, v, ch)}>
                                        <i className="fa-solid fa-check" /> {m ? 'Enregistrer' : 'Associer'}
                                    </button>
                                    {m && <button type="button" className="etu-btn etu-btn-neutre etu-btn-petit" onClick={() => setEdition(null)} title="Annuler la modification" aria-label="Annuler la modification"><i className="fa-solid fa-xmark" /></button>}
                                </td>
                            </tr>
                        );
                    })])}
                </tbody>
            </table>
            {!param.parcours.length && (
                <div className="etu-aide etu-aide-bloc"><i className="fa-solid fa-circle-info" /> Aucun parcours cette année dans le Programme : ils se créent dans
                    {' '}<strong>Programme formation → Programme national → Semestres</strong>.</div>
            )}
        </div>
    );
}

export default ImportScodocEtudiants;
